// What a token's status looks like on the board (issue 855): its conditions
// as badges along the lower edge, the marker ring's colour. Pure, so the rule
// is tested here and the canvas only draws what it is handed.
import { conditionWithLevel, MARKER_HEX, NONLETHAL_WORDS, dispositionOf, displayColour, markerHex, nonlethalStatus, sideHex, type Disposition, type Marker, type PaletteName, type TableTheme, type Values } from '@dragons-table/vtt-shared';

/** The mark of a creature holding a spell (docs/MRCS/dnd-5e.md §5.4), among its badges. */
export const CONCENTRATING_BADGE = 'Concentrating';

/** At most this many badges; the rest are one "+N". */
export const MAX_BADGES = 4;

const VOWELS = new Set(['a', 'e', 'i', 'o', 'u']);

/**
 * A condition's badge text. With room, its first word (at most eight letters); without, its first letter and the
 * next two consonants — "Prn" for Prone, "Shk" for Shaken, "Inv" for Invisible.
 */
export function abbreviate(name: string, roomy: boolean): string {
  const word = name.trim().split(/\s+/)[0] ?? '';
  if (roomy) return word.length <= 8 ? word : `${word.slice(0, 7)}…`;
  const first = word.charAt(0).toUpperCase();
  const rest = [...word.slice(1).toLowerCase()].filter((c) => /[a-z]/.test(c) && !VOWELS.has(c)).slice(0, 2).join('');
  return first + rest;
}

export interface Badges {
  /** The badges' texts, in the order the conditions were given. */
  shown: string[];
  /** How many were left off (drawn as "+N"); 0 for none. */
  more: number;
  /** The whole list, for the hover. */
  full: string;
}

/** The badges for a token's conditions: a few, abbreviated as the room allows, the rest counted. */
export function conditionBadges(conditions: readonly string[] | undefined, roomy: boolean, max = MAX_BADGES): Badges | null {
  const list = (conditions ?? []).map((c) => c.trim()).filter(Boolean);
  if (!list.length) return null;
  // With more than `max`, one place goes to the count, so the row never grows past `max` badges.
  const keep = list.length <= max ? list.length : max - 1;
  return { shown: list.slice(0, keep).map((c) => abbreviate(c, roomy)), more: list.length - keep, full: list.join(', ') };
}

/**
 * The conditions a token's badges show (issue 1502): what its nonlethal damage makes it by the rules — staggered or
 * unconscious, worked out from the hit points this viewer holds, never stored — first, then the conditions it was given.
 */
export function shownConditions(v: Pick<Values, 'hp' | 'conditions' | 'condition_levels'>, wounds: readonly string[] = []): string[] {
  const status = nonlethalStatus(v.hp);
  // A token's wound in words (issue 1526: "Moderate", "Unconscious", "Dying"), from the numbers this screen holds, first.
  const typed = (v.conditions ?? []).filter((c) => !wounds.some((w) => w.toLowerCase() === c.toLowerCase())).map((c) => conditionWithLevel(c, v.condition_levels));
  return [...wounds, ...(status ? [NONLETHAL_WORDS[status]] : []), ...typed];
}

/** The marker palette's standard colours (issue 855); each screen draws them in its person's look (issue 1041). */
export { MARKER_HEX };

/**
 * This screen's look (issue 1041): the person's palette and the site's theme, set by the shell from the settings
 * read at arrival. Display only: what is stored at the table is never mapped.
 */
let look: { palette: PaletteName; theme: TableTheme; text?: number } = { palette: 'standard', theme: 'dark' };
/** `text` is the person's text size as a scale (issue 1174): the nameplates are drawn that much larger. */
export const setLook = (next: { palette: PaletteName; theme: TableTheme; text?: number }): void => { look = next; };
export const getLook = () => look;
/** A marker's colour on this screen: the palette's, held to 3:1 on the board. */
export const markerColour = (m: Marker): number => parseInt(markerHex(m, look.palette, look.theme).slice(1), 16);
/** An identity colour on this screen, as `#rrggbb`. */
export const identityColour = (hex: string): string => displayColour(hex, look.palette, look.theme);
/**
 * A token without a picture is one shared disc (issue 1375): the same stone colour on every screen, so "the grey one"
 * means one token to everyone. An identity colour is a person's, never a token's (design/13 §6–7); the marker ring is
 * the table's shared colour control.
 */
export const PLAIN_DISC_HEX = '#78716c';
/** The plain disc's fill on this screen: the shared stone, only held to 3:1 on the board in this person's theme. */
export const plainDiscColour = (): number => parseInt(displayColour(PLAIN_DISC_HEX, 'standard', look.theme).slice(1), 16);
/**
 * Whose side outlines this screen draws (qa-runs#1157): `all` for the DM (and a co-DM shown the DM's board), `mine`
 * for a player — their own tokens, and another's whose side the DM revealed — and `none` when the person turned outlines off.
 */
export type SideOutlines = 'all' | 'mine' | 'none';
export const sideOutlines = (on: boolean, seesSides: boolean): SideOutlines => (!on ? 'none' : seesSides ? 'all' : 'mine');
/**
 * The side a token's outline shows on this screen, or null for none: a prop has no side; for a player, another token's
 * only once the DM revealed it (qa-runs#1157) — its `disposition` is then sent, and is absent until then.
 */
export function sideOutline(o: { kind: string; disposition?: Disposition; pc?: boolean; owners?: readonly string[]; mine?: boolean }, mode: SideOutlines): Disposition | null {
  if (mode === 'none' || o.kind !== 'token') return null;
  if (mode === 'mine' && o.mine !== true) return o.disposition ?? null;
  return dispositionOf(o);
}
/** A side's outline colour on this screen: the palette's green, blue or red, held to 3:1 on the board. */
export const sideColour = (d: Disposition): number => parseInt(sideHex(d, look.palette, look.theme).slice(1), 16);
export const markerLabel = (m: Marker): string => `${m.charAt(0).toUpperCase()}${m.slice(1)} marker`;

/** The frame's gold on the board (issue 1541): the selection, the ruler. The light floor takes the light theme's deeper gold. */
export const boardGold = (): number => (look.theme === 'light' ? 0x8a6820 : 0xd4a840);

/**
 * A token's initials on a plain disc (issue 1541): the first letters of its first two words, as Cinzel draws them. From
 * the name the viewer was sent; a withheld name (COMBAT-007) is absent, and so are its initials.
 */
export function initialsOf(name: string | undefined): string {
  const words = (name ?? '').split(/\s+/).map((w) => w.replace(/[^\p{L}\p{N}]/gu, '')).filter(Boolean);
  return words.slice(0, 2).map((w) => w.charAt(0).toUpperCase()).join('');
}

/**
 * Whether a token wears the small HP pips under its footprint rather than the bar along its top (issue 1541): a
 * creature the DM runs — a token, not a player character, owned by no player — whose health is among the values this
 * viewer was given. Nothing is computed for one whose health they were not sent.
 */
export function wearsHpPips(o: { kind: string; pc?: boolean; owners?: readonly string[] }, health: { current: number; max: number } | null): boolean {
  return o.kind === 'token' && !o.pc && (o.owners?.length ?? 0) === 0 && !!health && health.max > 0;
}
